import React, { useRef } from "react";
import {
  Box,
  Button,
  CardMedia,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useTranslation } from "react-i18next";
import {
  bob,
  motionSafe,
  rise,
  studioDialogBackdropSx,
  studioDialogPaperSx,
  swapSlide,
} from "./studioDialogMotion";

function isImageFile(file) {
  if (!file) return false;
  if ((file.type || "").startsWith("image/")) return true;
  return /\.(png|jpe?g|webp|gif|bmp|heic|heif|avif)$/i.test(file.name || "");
}

function ReplaceStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 88,
        mt: 1.25,
        mb: 0.5,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "20%",
          top: 16,
          width: 46,
          height: 56,
          borderRadius: 1.25,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.35),
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${swapSlide} 2.8s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          right: "20%",
          top: 16,
          width: 46,
          height: 56,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.12),
          border: "1.5px dashed",
          borderColor: alpha(accent, 0.5),
          animation: `${bob} 2s ease-in-out infinite`,
          ...motionSafe,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          width: 36,
          height: 36,
          borderRadius: "50%",
          bgcolor: alpha(accent, 0.16),
          border: "1px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          zIndex: 2,
        }}
      >
        <SwapHorizOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
    </Box>
  );
}

function ActionTile({ icon, label, onClick, accent, primary = false }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        cursor: "pointer",
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.85,
        px: 1.25,
        py: 1.6,
        borderRadius: 2.25,
        border: "1.5px solid",
        borderColor: primary ? alpha(accent, 0.45) : alpha(accent, 0.2),
        bgcolor: primary ? alpha(accent, 0.12) : alpha(accent, 0.04),
        color: primary ? accent : "text.primary",
        transition:
          "transform 160ms ease, box-shadow 160ms ease, background-color 160ms ease",
        boxShadow: primary ? `0 8px 18px ${alpha(accent, 0.16)}` : "none",
        "&:hover": {
          transform: "translateY(-2px)",
          bgcolor: primary ? alpha(accent, 0.18) : alpha(accent, 0.09),
          boxShadow: `0 10px 22px ${alpha(accent, 0.18)}`,
        },
        "&:active": { transform: "translateY(0)" },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
          "&:hover": { transform: "none" },
        },
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: primary ? accent : alpha(accent, 0.14),
          color: primary ? "#fff" : accent,
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 800,
          textAlign: "center",
          lineHeight: 1.2,
          fontSize: "0.82rem",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function ReplaceDesignRefDialog({
  open,
  onClose,
  previewUrl,
  fileName,
  index,
  onReplace,
  onRemove,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const accent = "#1473e6";
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const pickFile = (fileList) => {
    const file = Array.from(fileList || []).find(isImageFile);
    if (!file) return;
    onReplace?.(file);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={160}
      slotProps={{
        backdrop: { sx: studioDialogBackdropSx },
      }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{ sx: studioDialogPaperSx }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(
            accent,
            0.02,
          )} 55%)`,
          animation: `${rise} 280ms ease both`,
          ...motionSafe,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {t("presentacionesSearch.newDesign.replaceRefTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t("presentacionesSearch.newDesign.replaceRefLead", {
            n: typeof index === "number" ? index + 1 : "—",
          })}
        </Typography>
        <ReplaceStage accent={accent} />
      </Box>

      <DialogContent sx={{ pt: 2, pb: 2.5 }}>
        <Box
          sx={{
            borderRadius: 2.5,
            overflow: "hidden",
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.03),
            mb: 1.25,
            animation: `${rise} 340ms ease both`,
            ...motionSafe,
          }}
        >
          {previewUrl ? (
            <CardMedia
              component="img"
              image={previewUrl}
              alt={fileName || "reference"}
              sx={{
                width: "100%",
                maxHeight: 240,
                objectFit: "contain",
                display: "block",
              }}
            />
          ) : null}
        </Box>

        {fileName ? (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              maxWidth: "100%",
              px: 1,
              py: 0.35,
              mb: 1.5,
              borderRadius: 999,
              bgcolor: alpha(accent, 0.08),
              border: "1px solid",
              borderColor: alpha(accent, 0.18),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>
          </Box>
        ) : null}

        <Box
          sx={{
            display: "flex",
            gap: 1.1,
            mb: 1.75,
            animation: `${rise} 380ms ease both`,
            ...motionSafe,
          }}
        >
          <ActionTile
            primary
            accent={accent}
            icon={<CollectionsOutlinedIcon sx={{ fontSize: 22 }} />}
            label={t("presentacionesSearch.newDesign.replaceRefPick")}
            onClick={() => galleryRef.current?.click()}
          />
          <ActionTile
            accent={accent}
            icon={<PhotoCamera sx={{ fontSize: 22 }} />}
            label={t("presentacionesSearch.newDesign.takePhoto")}
            onClick={() => cameraRef.current?.click()}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            pt: 0.5,
          }}
        >
          <Button
            color="inherit"
            size="small"
            startIcon={<CloseOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              px: 1,
            }}
          >
            {t("presentacionesSearch.newDesign.replaceRefCancel")}
          </Button>
          <Button
            size="small"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
            onClick={() => onRemove?.()}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: theme.palette.error.main,
              bgcolor: alpha(theme.palette.error.main, 0.08),
              px: 1.25,
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.14),
              },
            }}
          >
            {t("presentacionesSearch.newDesign.replaceRefRemove")}
          </Button>
        </Box>

        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            pickFile(e.target.files);
            e.target.value = "";
          }}
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            pickFile(e.target.files);
            e.target.value = "";
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
